import React, { useState, useEffect } from 'react';
import clientCandidateDataService from '../../../services/clientCandidateDataService';
import useFeatureSettings from '../../../hooks/useFeatureSettings';

/**
 * EducationWidget
 * Displays and manages candidate's education and certifications with:
 * - Add/Edit/Delete education records
 * - Add/Edit/Delete certifications
 * - Tabs for Education vs Certifications
 * - Duration calculations
 */

const EducationWidget = ({ profile, candidateId, onRefresh, refreshTrigger }) => {
  // Education/certifications write through their own endpoints (not the
  // shared client-data PUT), so the org clean-trigger is applied here on
  // the client. Marks unclean only when 'education' is in the resolved set.
  const { settings: featureSettings } = useFeatureSettings();
  const markUncleanIfEnabled = async () => {
    if (!(featureSettings?.clean_trigger_widgets || []).includes('education')) return;
    try { await clientCandidateDataService.markAsUnclean(candidateId); }
    catch (e) { console.log('Could not mark as unclean:', e); }
  };
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('education');
  
  // Form states
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'education' or 'certification'
  
  const [eduFormData, setEduFormData] = useState({
    institution: '',
    degree_type: '',
    field_of_study: '',
    start_date: '',
    end_date: '',
    is_current: false,
    location: '',
    gpa: '',
    honors: ''
  });

  const [certFormData, setCertFormData] = useState({
    certification_name: '',
    issuing_organization: '',
    issue_date: '',
    expiration_date: '',
    credential_id: '',
    credential_url: ''
  });

  useEffect(() => {
    loadData();
  }, [candidateId, refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [eduResponse, certResponse] = await Promise.all([
        fetch(`/api/candidates/${candidateId}/education`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
        }),
        fetch(`/api/candidates/${candidateId}/certifications`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
        })
      ]);
      
      if (eduResponse.ok) {
        const eduData = await eduResponse.json();
        setEducation(eduData.sort((a, b) => new Date(b.start_date) - new Date(a.start_date)));
      }
      
      if (certResponse.ok) {
        const certData = await certResponse.json();
        setCertifications(certData.sort((a, b) => new Date(b.issue_date || 0) - new Date(a.issue_date || 0)));
      }
      
    } catch (err) {
      console.error('Error loading education data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForms = () => {
    setEduFormData({
      institution: '',
      degree_type: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      is_current: false,
      location: '',
      gpa: '',
      honors: ''
    });
    setCertFormData({
      certification_name: '',
      issuing_organization: '',
      issue_date: '',
      expiration_date: '',
      credential_id: '',
      credential_url: ''
    });
  };

  const handleAdd = (type) => {
    resetForms();
    setEditingId(null);
    setEditingType(type);
    setIsAdding(true);
  };

  const handleEditEducation = (edu) => {
    setEduFormData({
      institution: edu.institution || '',
      degree_type: edu.degree_type || '',
      field_of_study: edu.field_of_study || '',
      start_date: edu.start_date ? edu.start_date.substring(0, 7) : '',
      end_date: edu.end_date ? edu.end_date.substring(0, 7) : '',
      is_current: edu.is_current || false,
      location: edu.location || '',
      gpa: edu.gpa || '',
      honors: edu.honors || ''
    });
    setEditingId(edu.id);
    setEditingType('education');
    setIsAdding(false);
  };

  const handleEditCertification = (cert) => {
    setCertFormData({
      certification_name: cert.certification_name || '',
      issuing_organization: cert.issuing_organization || '',
      issue_date: cert.issue_date ? cert.issue_date.substring(0, 7) : '',
      expiration_date: cert.expiration_date ? cert.expiration_date.substring(0, 7) : '',
      credential_id: cert.credential_id || '',
      credential_url: cert.credential_url || ''
    });
    setEditingId(cert.id);
    setEditingType('certification');
    setIsAdding(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setEditingType(null);
    resetForms();
  };

  const handleSaveEducation = async () => {
    if (!eduFormData.institution || !eduFormData.degree_type || !eduFormData.start_date) {
      alert('Please fill in Institution, Degree Type, and Start Date');
      return;
    }

    try {
      const payload = {
        institution: eduFormData.institution,
        degree_type: eduFormData.degree_type,
        field_of_study: eduFormData.field_of_study || null,
        start_date: eduFormData.start_date + '-01',
        end_date: eduFormData.is_current ? null : (eduFormData.end_date ? eduFormData.end_date + '-01' : null),
        is_current: eduFormData.is_current,
        location: eduFormData.location || null,
        gpa: eduFormData.gpa || null,
        honors: eduFormData.honors || null
      };

      const url = editingId 
        ? `/api/candidates/${candidateId}/education/${editingId}`
        : `/api/candidates/${candidateId}/education`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await markUncleanIfEnabled();
        handleCancel();
        loadData();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving education:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  const handleSaveCertification = async () => {
    if (!certFormData.certification_name) {
      alert('Please fill in Certification Name');
      return;
    }

    try {
      const payload = {
        certification_name: certFormData.certification_name,
        issuing_organization: certFormData.issuing_organization || null,
        issue_date: certFormData.issue_date ? certFormData.issue_date + '-01' : null,
        expiration_date: certFormData.expiration_date ? certFormData.expiration_date + '-01' : null,
        credential_id: certFormData.credential_id || null,
        credential_url: certFormData.credential_url || null
      };

      const url = editingId 
        ? `/api/candidates/${candidateId}/certifications/${editingId}`
        : `/api/candidates/${candidateId}/certifications`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await markUncleanIfEnabled();
        handleCancel();
        loadData();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving certification:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDeleteEducation = async (eduId) => {
    if (!window.confirm('Delete this education record?')) return;

    try {
      const response = await fetch(`/api/candidates/${candidateId}/education/${eduId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      if (response.ok) {
        await markUncleanIfEnabled();
        loadData();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Error deleting education:', err);
      alert('Failed to delete');
    }
  };

  const handleDeleteCertification = async (certId) => {
    if (!window.confirm('Delete this certification?')) return;

    try {
      const response = await fetch(`/api/candidates/${candidateId}/certifications/${certId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      if (response.ok) {
        await markUncleanIfEnabled();
        loadData();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Error deleting certification:', err);
      alert('Failed to delete');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    let months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
    if (months < 0) months = 0;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years === 0) return `${remainingMonths} mo`;
    if (remainingMonths === 0) return `${years} yr${years > 1 ? 's' : ''}`;
    return `${years} yr${years > 1 ? 's' : ''}, ${remainingMonths} mo`;
  };

  // Education Form
  const renderEducationForm = () => (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
      <h4 className="font-medium text-sm text-emerald-900 mb-3">
        {editingId ? 'Edit Education' : 'Add Education'}
      </h4>
      
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Institution/School *</label>
          <input
            type="text"
            value={eduFormData.institution}
            onChange={(e) => setEduFormData({ ...eduFormData, institution: e.target.value })}
            className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
            placeholder="University name"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Degree Type *</label>
            <select
              value={eduFormData.degree_type}
              onChange={(e) => setEduFormData({ ...eduFormData, degree_type: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
            >
              <option value="">Select...</option>
              <option value="High School">High School</option>
              <option value="Associate's">Associate's Degree</option>
              <option value="Bachelor's">Bachelor's Degree</option>
              <option value="Master's">Master's Degree</option>
              <option value="MBA">MBA</option>
              <option value="PhD">PhD</option>
              <option value="JD">JD (Law)</option>
              <option value="MD">MD (Medical)</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Field of Study</label>
            <input
              type="text"
              value={eduFormData.field_of_study}
              onChange={(e) => setEduFormData({ ...eduFormData, field_of_study: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
              placeholder="e.g., Computer Science"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
            <input
              type="month"
              value={eduFormData.start_date}
              onChange={(e) => setEduFormData({ ...eduFormData, start_date: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="month"
              value={eduFormData.end_date}
              onChange={(e) => setEduFormData({ ...eduFormData, end_date: e.target.value })}
              disabled={eduFormData.is_current}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="edu_is_current"
            checked={eduFormData.is_current}
            onChange={(e) => setEduFormData({ ...eduFormData, is_current: e.target.checked, end_date: '' })}
            className="rounded border-gray-300 text-emerald-700"
          />
          <label htmlFor="edu_is_current" className="text-sm text-gray-700">Currently attending</label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={eduFormData.location}
              onChange={(e) => setEduFormData({ ...eduFormData, location: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
              placeholder="City, State"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">GPA</label>
            <input
              type="text"
              value={eduFormData.gpa}
              onChange={(e) => setEduFormData({ ...eduFormData, gpa: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
              placeholder="3.8"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Honors</label>
            <input
              type="text"
              value={eduFormData.honors}
              onChange={(e) => setEduFormData({ ...eduFormData, honors: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
              placeholder="Magna Cum Laude"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleSaveEducation} className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700">
            {editingId ? 'Update' : 'Add Education'}
          </button>
          <button onClick={handleCancel} className="px-3 py-1.5 border text-sm rounded hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Certification Form
  const renderCertificationForm = () => (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
      <h4 className="font-medium text-sm text-green-900 mb-3">
        {editingId ? 'Edit Certification' : 'Add Certification'}
      </h4>
      
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Certification Name *</label>
          <input
            type="text"
            value={certFormData.certification_name}
            onChange={(e) => setCertFormData({ ...certFormData, certification_name: e.target.value })}
            className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-green-500"
            placeholder="e.g., CFA Level II"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Issuing Organization</label>
          <input
            type="text"
            value={certFormData.issuing_organization}
            onChange={(e) => setCertFormData({ ...certFormData, issuing_organization: e.target.value })}
            className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-green-500"
            placeholder="e.g., CFA Institute"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Issue Date</label>
            <input
              type="month"
              value={certFormData.issue_date}
              onChange={(e) => setCertFormData({ ...certFormData, issue_date: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
            <input
              type="month"
              value={certFormData.expiration_date}
              onChange={(e) => setCertFormData({ ...certFormData, expiration_date: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-green-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Credential ID</label>
            <input
              type="text"
              value={certFormData.credential_id}
              onChange={(e) => setCertFormData({ ...certFormData, credential_id: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-green-500"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Credential URL</label>
            <input
              type="url"
              value={certFormData.credential_url}
              onChange={(e) => setCertFormData({ ...certFormData, credential_url: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-green-500"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleSaveCertification} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700">
            {editingId ? 'Update' : 'Add Certification'}
          </button>
          <button onClick={handleCancel} className="px-3 py-1.5 border text-sm rounded hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p className="text-sm">{error}</p>
        <button onClick={loadData} className="mt-2 text-xs text-emerald-700 hover:underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => { setActiveTab('education'); handleCancel(); }}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
            activeTab === 'education' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Education ({education.length})
        </button>
        <button
          onClick={() => { setActiveTab('certifications'); handleCancel(); }}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
            activeTab === 'certifications' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Certifications ({certifications.length})
        </button>
      </div>

      {/* Education Tab */}
      {activeTab === 'education' && (
        <div>
          {/* Add button */}
          {!isAdding && !editingId && (
            <button
              onClick={() => handleAdd('education')}
              className="w-full mb-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Education
            </button>
          )}

          {/* Form */}
          {(isAdding || editingId) && editingType === 'education' && renderEducationForm()}

          {/* List */}
          {education.length === 0 && !isAdding ? (
            <div className="text-center py-6 text-gray-500">
              <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
              <p className="text-sm">No education history</p>
            </div>
          ) : (
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="flex gap-3 group">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{edu.institution}</h4>
                        <p className="text-sm text-gray-700">
                          {edu.degree_type}
                          {edu.field_of_study && <span className="text-gray-500"> in {edu.field_of_study}</span>}
                        </p>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button onClick={() => handleEditEducation(edu)} className="p-1 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded" title="Edit">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => handleDeleteEducation(edu.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(edu.start_date)} – {edu.is_current ? <span className="text-green-600 font-medium">Present</span> : formatDate(edu.end_date)}
                      {edu.start_date && <span className="text-gray-400 ml-1">· {calculateDuration(edu.start_date, edu.is_current ? null : edu.end_date)}</span>}
                    </p>
                    {edu.location && <p className="text-xs text-gray-500 mt-0.5">📍 {edu.location}</p>}
                    {(edu.gpa || edu.honors) && (
                      <div className="flex gap-2 mt-1">
                        {edu.gpa && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">GPA: {edu.gpa}</span>}
                        {edu.honors && <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">{edu.honors}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Certifications Tab */}
      {activeTab === 'certifications' && (
        <div>
          {/* Add button */}
          {!isAdding && !editingId && (
            <button
              onClick={() => handleAdd('certification')}
              className="w-full mb-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-green-400 hover:text-green-600 hover:bg-green-50 transition-colors flex items-center justify-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Certification
            </button>
          )}

          {/* Form */}
          {(isAdding || editingId) && editingType === 'certification' && renderCertificationForm()}

          {/* List */}
          {certifications.length === 0 && !isAdding ? (
            <div className="text-center py-6 text-gray-500">
              <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <p className="text-sm">No certifications</p>
            </div>
          ) : (
            <div className="space-y-4">
              {certifications.map((cert) => (
                <div key={cert.id} className="flex gap-3 group">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{cert.certification_name}</h4>
                        {cert.issuing_organization && <p className="text-sm text-gray-600">{cert.issuing_organization}</p>}
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button onClick={() => handleEditCertification(cert)} className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded" title="Edit">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => handleDeleteCertification(cert.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {cert.issue_date && <>Issued {formatDate(cert.issue_date)}</>}
                      {cert.expiration_date && (
                        <span className={new Date(cert.expiration_date) < new Date() ? 'text-red-500 ml-2' : 'ml-2'}>
                          · Expires {formatDate(cert.expiration_date)}
                        </span>
                      )}
                      {!cert.expiration_date && cert.issue_date && <span className="text-green-600 ml-2">· No Expiration</span>}
                    </p>
                    {(cert.credential_id || cert.credential_url) && (
                      <div className="mt-1 flex items-center gap-2">
                        {cert.credential_id && <span className="text-xs text-gray-500">ID: {cert.credential_id}</span>}
                        {cert.credential_url && (
                          <a href={cert.credential_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline flex items-center gap-0.5">
                            View credential
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EducationWidget;
